// Camera acquisition, lens switching, zoom (native + digital), torch, lifecycle.
// Everything capability-detected; no UA sniffing (SKILL gate 3).

export interface CameraInfo {
  deviceId: string;
  label: string;
}

export interface ZoomState {
  /** effective zoom shown to the user = native * digital */
  effective: number;
  native: number;
  digital: number;
  min: number;
  max: number;
  nativeSupported: boolean;
}

export interface CameraState {
  streaming: boolean;
  cameras: CameraInfo[];
  activeDeviceId: string | null;
  torchSupported: boolean;
  torchOn: boolean;
  zoom: ZoomState;
  frameW: number;
  frameH: number;
  mirrored: boolean;
  error: string | null;
}

type Listener = (s: CameraState) => void;

interface ZoomCaps {
  min: number;
  max: number;
  step?: number;
}

// MediaTrackCapabilities zoom/torch are not in all TS lib versions; narrow locally.
interface ExtCapabilities extends MediaTrackCapabilities {
  zoom?: ZoomCaps;
  torch?: boolean;
  facingMode?: string[];
}

const LS_KEY = 'autospy.camera.v1';
const DIGITAL_MAX = 4;

export type CameraFacing = 'environment' | 'user' | 'unknown';

/**
 * Classify a camera's facing without opening a stream. Capability API first
 * (InputDeviceInfo.getCapabilities — reliable on Android Chrome/Brave), label
 * text only as a last resort, never UA sniffing. Exported for unit tests.
 */
export function classifyCameraFacing(
  label: string,
  caps?: { facingMode?: string[] } | undefined,
): CameraFacing {
  const fm = caps?.facingMode;
  if (fm?.includes('environment')) return 'environment';
  if (fm?.includes('user')) return 'user';
  const l = label.toLowerCase();
  if (/\b(back|rear|environment|world)\b|задн|tagakaamera/.test(l)) return 'environment';
  if (/\b(front|user|selfie|face)\b|фронт|esikaamera/.test(l)) return 'user';
  return 'unknown';
}

/**
 * Best-effort ultra-wide detection from a camera label. The ultra-wide lens is
 * useless for reading plates — its wide FOV forces heavy digital zoom, which
 * degrades OCR — so it is dropped from the lens cycle. Only matches clearly
 * ultra-wide labels; the plain "wide" main camera and generic labels
 * ("camera2 0, facing back") return false, so there is no regression on
 * devices that don't describe their lenses. Exported for unit tests.
 */
export function isLikelyUltrawide(label: string): boolean {
  const l = label.toLowerCase();
  return /ultra[\s-]?wide|ultrawide|\buw\b|\b0\.5\s?x/.test(l);
}

export interface CameraCandidate {
  deviceId: string;
  label: string;
  facing: CameraFacing;
  /** width.max × height.max from device capabilities; 0/undefined when unknown. */
  maxArea?: number | undefined;
}

/**
 * Order the enumerated cameras into the usable lens cycle. Rear lenses only
 * (selfie camera can't scan plates), labeled ultra-wides dropped, and the main
 * sensor first. Samsung labels ("camera2 2, facing back") carry no lens hint,
 * so the main lens is identified by capture resolution instead: on every
 * multi-lens phone the main sensor's max resolution dwarfs the ultra-wide's
 * (S25 Ultra: 200 MP vs 50 MP). The sort is stable, so devices without
 * resolution capabilities keep enumeration order — where Android lists the
 * primary rear camera (id 0) first. Exported for unit tests.
 */
export function selectUsableCameras<T extends CameraCandidate>(all: T[]): T[] {
  const rear = all.filter((c) => c.facing === 'environment');
  const rearNoUltrawide = rear.filter((c) => !isLikelyUltrawide(c.label));
  const usable = rearNoUltrawide.length > 0 ? rearNoUltrawide : rear.length > 0 ? rear : all;
  return [...usable].sort((a, b) => (b.maxArea ?? 0) - (a.maxArea ?? 0));
}

interface Persisted {
  deviceId?: string;
  zoom?: number;
}

function loadPersisted(): Persisted {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) ?? '{}') as Persisted;
  } catch {
    return {};
  }
}

function persist(p: Persisted): void {
  const cur = loadPersisted();
  localStorage.setItem(LS_KEY, JSON.stringify({ ...cur, ...p }));
}

export class CameraEngine {
  private video: HTMLVideoElement;
  private stream: MediaStream | null = null;
  private track: MediaStreamTrack | null = null;
  private listeners = new Set<Listener>();
  private nativeZoomCaps: ZoomCaps | null = null;
  private onVisibility = () => {
    if (document.visibilityState === 'visible') void this.reacquireIfDead();
  };

  state: CameraState = {
    streaming: false,
    cameras: [],
    activeDeviceId: null,
    torchSupported: false,
    torchOn: false,
    zoom: { effective: 1, native: 1, digital: 1, min: 1, max: DIGITAL_MAX, nativeSupported: false },
    frameW: 0,
    frameH: 0,
    mirrored: false,
    error: null,
  };

  constructor(video: HTMLVideoElement) {
    this.video = video;
    // iOS requires these before play() resolves in a PWA context.
    video.playsInline = true;
    video.muted = true;
    video.autoplay = true;
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  onChange(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    for (const fn of this.listeners) fn({ ...this.state, zoom: { ...this.state.zoom } });
  }

  async start(deviceId?: string): Promise<void> {
    this.stopTracks();
    const persisted = loadPersisted();
    // Only an explicit request (initial default, or the lens-cycle button) picks
    // a device. We deliberately do NOT restore a persisted deviceId on cold
    // start: that reopened whatever lens was last cycled to (e.g. the ultra-wide
    // on Samsung), so every launch now starts on the OS-default rear camera.
    const wantedDevice = deviceId;
    const video: MediaTrackConstraints = wantedDevice
      ? { deviceId: { exact: wantedDevice }, width: { ideal: 1920 }, height: { ideal: 1080 } }
      : {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        };
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ video, audio: false });
    } catch (e) {
      // A persisted device may have vanished — retry once with the generic constraint.
      if (wantedDevice) {
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
      } else {
        this.state.error = e instanceof Error ? e.name : 'CameraError';
        this.emit();
        throw e;
      }
    }
    this.track = this.stream.getVideoTracks()[0] ?? null;
    this.video.srcObject = this.stream;
    await this.video.play().catch(() => undefined); // autoplay policies: already muted+playsinline

    const settings = this.track?.getSettings() ?? {};
    this.state.activeDeviceId = settings.deviceId ?? null;
    this.state.frameW = settings.width ?? this.video.videoWidth;
    this.state.frameH = settings.height ?? this.video.videoHeight;
    this.state.mirrored = settings.facingMode === 'user';
    this.state.streaming = true;
    this.state.error = null;

    this.readCapabilities();
    await this.refreshCameraList(); // labels only exist after permission
    // The OS default for facingMode=environment is the ultra-wide on some
    // Samsungs (S2x Ultra), and their labels give no lens hint to filter on
    // before the stream opens. Now that the ranked list exists, hop to the
    // preferred lens (cameras[0]: the main sensor) if the default pick was
    // anything else. Only on a default start — an explicit deviceId (lens
    // cycle, reacquire) is honored as-is, so this cannot recurse.
    if (!wantedDevice) {
      const preferred = this.state.cameras[0]?.deviceId;
      if (preferred && this.state.activeDeviceId && preferred !== this.state.activeDeviceId) {
        await this.start(preferred);
        return;
      }
    }
    // Restore persisted zoom on the new track.
    await this.setZoom(persisted.zoom ?? 1);
    this.emit();
  }

  private readCapabilities(): void {
    const caps = (this.track?.getCapabilities?.() ?? {}) as ExtCapabilities;
    this.nativeZoomCaps = caps.zoom && caps.zoom.max > caps.zoom.min ? caps.zoom : null;
    this.state.torchSupported = caps.torch === true;
    this.state.torchOn = false;
    const nMin = this.nativeZoomCaps?.min ?? 1;
    const nMax = this.nativeZoomCaps?.max ?? 1;
    this.state.zoom = {
      effective: 1,
      native: nMin,
      digital: 1,
      // Floor at 1×: logical multi-cameras expose zoom below 1 (0.5×–0.6×),
      // which is the ultra-wide by another door — never allow it.
      min: Math.max(1, nMin),
      max: nMax * DIGITAL_MAX,
      nativeSupported: this.nativeZoomCaps !== null,
    };
  }

  async refreshCameraList(): Promise<void> {
    const devs = await navigator.mediaDevices.enumerateDevices();
    const all = devs
      .filter((d) => d.kind === 'videoinput')
      .map((d, i) => {
        const caps = (d as InputDeviceInfo).getCapabilities?.() as ExtCapabilities | undefined;
        return {
          deviceId: d.deviceId,
          label: d.label || `Camera ${i + 1}`,
          facing: classifyCameraFacing(d.label, caps),
          maxArea: (caps?.width?.max ?? 0) * (caps?.height?.max ?? 0),
        };
      });
    this.state.cameras = selectUsableCameras(all).map(({ deviceId, label }) => ({
      deviceId,
      label,
    }));
    this.emit();
  }

  /** Cycle to the next camera (lens toggle shows only when >1 device exists). */
  async switchNext(): Promise<void> {
    const { cameras, activeDeviceId } = this.state;
    if (cameras.length < 2) return;
    const idx = cameras.findIndex((c) => c.deviceId === activeDeviceId);
    const next = cameras[(idx + 1) % cameras.length];
    if (next) await this.start(next.deviceId);
  }

  /**
   * One slider: effective zoom = native × digital. Native range is used first
   * (it raises true optical/sensor detail), digital crop extends past it.
   */
  async setZoom(effective: number): Promise<void> {
    const z = this.state.zoom;
    const clamped = Math.min(Math.max(effective, z.min), z.max);
    let native = 1;
    let digital = clamped;
    if (this.nativeZoomCaps && this.track) {
      const { min, max } = this.nativeZoomCaps;
      native = Math.min(Math.max(clamped, min), max);
      digital = Math.max(1, clamped / native);
      try {
        await this.track.applyConstraints({
          advanced: [{ zoom: native } as MediaTrackConstraintSet],
        });
      } catch {
        // Native zoom rejected at runtime — degrade to pure digital.
        native = 1;
        digital = clamped;
      }
    }
    this.state.zoom = { ...z, effective: clamped, native, digital };
    persist({ zoom: clamped });
    this.emit();
  }

  async toggleTorch(): Promise<void> {
    if (!this.state.torchSupported || !this.track) return;
    const next = !this.state.torchOn;
    try {
      await this.track.applyConstraints({
        advanced: [{ torch: next } as MediaTrackConstraintSet],
      });
      this.state.torchOn = next;
      this.emit();
    } catch {
      // capability lied — hide the button
      this.state.torchSupported = false;
      this.emit();
    }
  }

  /** iOS kills backgrounded streams; re-acquire when we come back. */
  private async reacquireIfDead(): Promise<void> {
    if (!this.state.streaming) return;
    if (!this.track || this.track.readyState === 'ended') {
      await this.start(this.state.activeDeviceId ?? undefined).catch(() => undefined);
    }
  }

  get videoEl(): HTMLVideoElement {
    return this.video;
  }

  get digitalZoom(): number {
    return this.state.zoom.digital;
  }

  private stopTracks(): void {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.track = null;
  }

  stop(): void {
    this.stopTracks();
    this.state.streaming = false;
    this.emit();
  }

  destroy(): void {
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.stop();
    this.listeners.clear();
  }
}
